import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertCircle, Lock, Box, Gavel, Calendar } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from '@/components/icons';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';

type QuantityKind = 'mass' | 'liquid';

interface QuickProductFormProps {
  productType: string;
  prefilledData?: {
    title: string;
    brand: string;
    content: string;
    model: string;
  };
  
  // Form state
  quantityKind: QuantityKind;
  quantityValue: string;
  quantityUnit: string;
  stock: string;
  price: string;
  model: string;
  size: string;
  color: string;
  
  // Auction fields
  isAuction: boolean;
  startingBid: string;
  buyNowPrice: string;
  auctionStartTime?: Date;
  auctionEndTime?: Date;
  
  // Delivery options
  deliveryByMinibid: boolean;
  deliveryLocal: boolean;
  deliveryAgree: boolean;
  
  // Handlers
  onQuantityKindChange: (kind: QuantityKind) => void;
  onQuantityValueChange: (value: string) => void;
  onQuantityUnitChange: (unit: string) => void;
  onStockChange: (value: string) => void;
  onPriceChange: (value: string) => void;
  onModelChange?: (value: string) => void;
  onSizeChange?: (value: string) => void;
  onColorChange?: (value: string) => void;
  onIsAuctionChange: (value: boolean) => void;
  onStartingBidChange: (value: string) => void;
  onBuyNowPriceChange: (value: string) => void;
  onAuctionStartTimeChange?: (date: Date | undefined) => void;
  onAuctionEndTimeChange?: (date: Date | undefined) => void;
  onDeliveryByMinibidChange: (value: boolean) => void;
  onDeliveryLocalChange: (value: boolean) => void;
  onDeliveryAgreeChange: (value: boolean) => void;
}

/**
 * Dynamic form renderer for quick product creation
 * Shows different fields based on product type
 * Prefilled fields are locked to prevent accidental changes
 */
export default function QuickProductForm({
  productType,
  prefilledData,
  quantityKind,
  quantityValue,
  quantityUnit,
  stock,
  price,
  model,
  size,
  color,
  isAuction,
  startingBid,
  buyNowPrice,
  auctionStartTime,
  auctionEndTime,
  deliveryByMinibid,
  deliveryLocal,
  deliveryAgree,
  onQuantityKindChange,
  onQuantityValueChange,
  onQuantityUnitChange,
  onStockChange,
  onPriceChange,
  onModelChange,
  onSizeChange,
  onColorChange,
  onIsAuctionChange,
  onStartingBidChange,
  onBuyNowPriceChange,
  onAuctionStartTimeChange,
  onAuctionEndTimeChange,
  onDeliveryByMinibidChange,
  onDeliveryLocalChange,
  onDeliveryAgreeChange,
}: QuickProductFormProps) {
  const isGroceries = productType === 'groceries';
  const isElectronics = productType === 'electronics';
  const isClothing = productType === 'clothing';
  const isVehicle = productType === 'vehicle';

  return (
    <div className="space-y-4">
      {/* Prefilled Data Badge */}
      {prefilledData && (prefilledData.title || prefilledData.brand) && (
        <Card className="border-primary/25 bg-primary/10">
          <CardContent className="pt-4">
            <div className="flex items-start gap-2 text-xs">
              <div className="mt-0.5 text-primary">
                <Lock className="h-3.5 w-3.5" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-primary">Datos del catálogo</div>
                <div className="text-primary/80 mt-1 space-y-0.5">
                  {prefilledData.title && <div><strong>Producto:</strong> {prefilledData.title}</div>}
                  {prefilledData.brand && <div><strong>Marca:</strong> {prefilledData.brand}</div>}
                  {prefilledData.content && <div><strong>Contenido:</strong> {prefilledData.content}</div>}
                  {prefilledData.model && <div><strong>Modelo:</strong> {prefilledData.model}</div>}
                </div>
                <div className="text-primary/60 text-[10px] mt-2 italic">Estos campos están bloqueados para mantener consistencia</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Model/Specs Section (for electronics only, not vehicles) */}
      {isElectronics && onModelChange && (
        <div className="space-y-2">
          <Label htmlFor="model" className="font-semibold">
            Modelo/Especificación
          </Label>
          <Input
            id="model"
            value={model}
            onChange={(e) => onModelChange(e.target.value)}
            placeholder="Ej: iPhone 15 Pro"
          />
        </div>
      )}

      {/* Color/Appearance Section (for clothing/electronics) */}
      {(isClothing || isElectronics) && (
        <div className="grid grid-cols-2 gap-3">
          {isClothing && onSizeChange && (
            <div className="space-y-2">
              <Label htmlFor="size" className="font-semibold">Talla</Label>
              <Input
                id="size"
                value={size}
                onChange={(e) => onSizeChange(e.target.value)}
                placeholder="XS / S / M / L / XL"
              />
            </div>
          )}
          {onColorChange && (
            <div className="space-y-2">
              <Label htmlFor="color" className="font-semibold">Color</Label>
              <Input
                id="color"
                value={color}
                onChange={(e) => onColorChange(e.target.value)}
                placeholder="Ej: Negro, Azul"
              />
            </div>
          )}
        </div>
      )}

      {/* Pricing Mode Selector */}
      <div className="space-y-3 border-t pt-4">
        <div className="font-semibold text-sm">Tipo de venta</div>
        <RadioGroup value={isAuction ? 'auction' : 'fixed'} onValueChange={(val) => onIsAuctionChange(val === 'auction')}>
          <div className="flex items-center gap-3 p-2 rounded border border-primary/30 cursor-pointer hover:bg-primary/5 transition-colors">
            <RadioGroupItem value="fixed" id="pricing-fixed" />
            <Label htmlFor="pricing-fixed" className="flex-1 cursor-pointer font-normal">
              <span className="font-semibold">Precio fijo</span>
              <span className="text-xs text-muted-foreground ml-2">Venta directa</span>
            </Label>
          </div>
          <div className="flex items-center gap-3 p-2 rounded border border-primary/30 cursor-pointer hover:bg-primary/5 transition-colors">
            <RadioGroupItem value="auction" id="pricing-auction" />
            <Label htmlFor="pricing-auction" className="flex-1 cursor-pointer font-normal">
              <span className="font-semibold flex items-center gap-1">
                <Gavel className="h-4 w-4" />
                Subastar este producto
              </span>
              <span className="text-xs text-muted-foreground ml-6">Pujas competitivas</span>
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Stock & Price Section - Changes based on pricing mode */}
      {!isAuction ? (
        <div className="grid grid-cols-2 gap-3">
          {/* Stock Input with Icon */}
          <div className="space-y-2">
            <Label htmlFor="stock" className="font-semibold text-xs uppercase tracking-wide text-muted-foreground">
              Stock
              <span className="text-destructive ml-1">*</span>
            </Label>
            <div className="relative flex items-center">
              <Box className="absolute left-3 h-4 w-4 text-primary pointer-events-none" />
              <Input
                id="stock"
                type="number"
                min="0"
                value={stock}
                onChange={(e) => onStockChange(e.target.value)}
                placeholder="0"
                className="pl-10 pr-3 h-11 rounded-full border-primary/30 font-semibold text-sm bg-background border-2 focus:border-primary"
              />
            </div>
          </div>

          {/* Price Input with Icon */}
          <div className="space-y-2">
            <Label htmlFor="price" className="font-semibold text-xs uppercase tracking-wide text-muted-foreground">
              Precio
              <span className="text-destructive ml-1">*</span>
            </Label>
            <div className="relative flex items-center">
              <span className="absolute left-3 text-primary font-semibold text-sm pointer-events-none">$</span>
              <Input
                id="price"
                type="number"
                min="0"
                step="0.01"
                value={price}
                onChange={(e) => onPriceChange(e.target.value)}
                placeholder="0"
                className="pl-10 pr-3 h-11 rounded-full border-primary/30 font-semibold text-sm bg-background border-2 focus:border-primary"
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3 bg-primary/5 border border-primary/20 rounded-lg p-3">
          {/* Auction Puja Inicial */}
          <div className="space-y-2">
            <Label htmlFor="starting-bid" className="font-semibold text-xs uppercase tracking-wide text-muted-foreground">
              Puja Inicial
              <span className="text-destructive ml-1">*</span>
            </Label>
            <div className="relative flex items-center">
              <span className="absolute left-3 text-primary font-semibold text-sm pointer-events-none">$</span>
              <Input
                id="starting-bid"
                type="number"
                min="0"
                step="0.01"
                value={startingBid}
                onChange={(e) => onStartingBidChange(e.target.value)}
                placeholder="0"
                className="pl-10 pr-3 h-11 rounded-full border-primary/30 font-semibold text-sm bg-background border-2 focus:border-primary"
              />
            </div>
          </div>

          {/* Buy Now Price (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="buy-now-price" className="font-semibold text-xs uppercase tracking-wide text-muted-foreground">
              Precio de Compra ya (opcional)
            </Label>
            <div className="relative flex items-center">
              <span className="absolute left-3 text-primary font-semibold text-sm pointer-events-none">$</span>
              <Input
                id="buy-now-price"
                type="number"
                min="0"
                step="0.01"
                value={buyNowPrice}
                onChange={(e) => onBuyNowPriceChange(e.target.value)}
                placeholder="Opcional"
                className="pl-10 pr-3 h-11 rounded-full border-primary/30 font-semibold text-sm bg-background border-2 focus:border-primary"
              />
            </div>
          </div>

          {/* Auction Start Time */}
          <div className="space-y-2">
            <Label className="font-semibold text-xs uppercase tracking-wide text-muted-foreground">
              Fecha y Hora de Inicio
              <span className="text-destructive ml-1">*</span>
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={"outline"}
                  className={cn(
                    "w-full justify-start text-left font-normal h-11 rounded-full",
                    !auctionStartTime && "text-muted-foreground"
                  )}
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  {auctionStartTime ? format(auctionStartTime, "PPP HH:mm", { locale: es }) : <span>Selecciona fecha y hora</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[280px] p-0 z-[9999]">
                {/* Simple date/time input for quick form */}
                <div className="p-3 space-y-3 border-b">
                  <Input
                    type="datetime-local"
                    value={auctionStartTime ? format(auctionStartTime, "yyyy-MM-dd'T'HH:mm") : ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        const date = new Date(e.target.value);
                        onAuctionStartTimeChange?.(date);
                      }
                    }}
                    className="h-10 rounded-md border border-primary/30"
                  />
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Auction End Time */}
          <div className="space-y-2">
            <Label className="font-semibold text-xs uppercase tracking-wide text-muted-foreground">
              Fecha y Hora de Fin
              <span className="text-destructive ml-1">*</span>
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={"outline"}
                  className={cn(
                    "w-full justify-start text-left font-normal h-11 rounded-full",
                    !auctionEndTime && "text-muted-foreground"
                  )}
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  {auctionEndTime ? format(auctionEndTime, "PPP HH:mm", { locale: es }) : <span>Selecciona fecha y hora</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[280px] p-0 z-[9999]">
                {/* Simple date/time input for quick form */}
                <div className="p-3 space-y-3 border-b">
                  <Input
                    type="datetime-local"
                    value={auctionEndTime ? format(auctionEndTime, "yyyy-MM-dd'T'HH:mm") : ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        const date = new Date(e.target.value);
                        onAuctionEndTimeChange?.(date);
                      }
                    }}
                    className="h-10 rounded-md border border-primary/30"
                  />
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      )}

      {/* Delivery Options */}
      <div className="space-y-3 border-t pt-4">
        <div className="font-semibold text-sm">Opciones de entrega</div>
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={deliveryByMinibid}
              onCheckedChange={(checked) => onDeliveryByMinibidChange(Boolean(checked))}
            />
            <span className="text-sm">Envío por Minibid</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={deliveryLocal}
              onCheckedChange={(checked) => onDeliveryLocalChange(Boolean(checked))}
            />
            <span className="text-sm">Retiro Local</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={deliveryAgree}
              onCheckedChange={(checked) => onDeliveryAgreeChange(Boolean(checked))}
            />
            <span className="text-sm">Acordar con vendedor</span>
          </label>
        </div>
      </div>
    </div>
  );
}
